import { Activity, ActivityType } from '../../../../api/activities/types'
import { ContactPropertyTransaction } from '../../../../api/contacts/types'
import { FormValue } from '../../../../api/globalTypes'
import { Property } from '../../../../api/properties/types'
import { REAL_ESTATE_SERVICE } from '../../../../common/constants'
import { formatDateTime } from '../../../../utils/general'

type ConnectedPropertyKey = 'contactBuyer' | 'contactSeller' | 'contactSelectedActivities'

export interface ConnectedPropertyRow {
    status: FormValue | ActivityType
    date: string
    id: number
    internalAppId: number
    type: FormValue | string
    city: string
    street: string
    price: number | string
    service: FormValue | string
}

export interface ConnectedPropertiesResponse {
    contactBuyer: ContactPropertyTransaction[]
    contactSeller: Property[]
    contactSelectedActivities: Activity[]
}

export interface ConnectedPropertiesFilterData {
    sales: ConnectedPropertyRow[]
    lease: ConnectedPropertyRow[]
    purchase: ConnectedPropertyRow[]
    rent: ConnectedPropertyRow[]
}

/**
 * Maps and transforms raw API response data into a standardized format for table display.
 * Handles different data structures based on the contact role (buyer, seller, or selected activities).
 */
const mapTableData = (arr: ContactPropertyTransaction[] | Property[] | Activity[], key: ConnectedPropertyKey): ConnectedPropertyRow[] => {
    switch (key) {
        case 'contactBuyer':
            return (arr as ContactPropertyTransaction[]).map((transaction) => ({
                status: transaction.propertyStatus,
                date: formatDateTime(transaction.conclusionDate ?? transaction.reservationDate),
                id: transaction.id,
                internalAppId: transaction.internalAppId,
                type: transaction.propertyType || '',
                city: transaction.city || '',
                street: `${transaction.street || ''} ${transaction.streetNumber || ''}`,
                price: transaction.price || '',
                service: transaction.propertyService || '',
            }))
        case 'contactSeller':
            return (arr as Property[]).map((property) => ({
                status: property.propertyStatus,
                date: formatDateTime(property.propertyTransaction?.conclusionDate ?? property.created),
                id: property.id as number,
                internalAppId: property.internalAppId,
                type: property.propertyType || '',
                city: property.city.name || '',
                street: `${property.street || ''} ${property.streetNumber || ''}`,
                price: property.price || '',
                service: property.propertyService || '',
            }))
        case 'contactSelectedActivities':
            return (arr as Activity[])
                .map((activity): ConnectedPropertyRow | null => {
                    const property = activity.property?.[0]
                    if (!property) return null

                    return {
                        status: activity.activityType,
                        date: formatDateTime(activity.dateFrom),
                        id: property.id as number,
                        internalAppId: property.internalAppId,
                        type: property.propertyType || '',
                        city: property.city.name || '',
                        street: `${property.street || ''} ${property.streetNumber || ''}`,
                        price: property.price || '',
                        service: property.propertyService || '',
                    }
                })
                .filter((row): row is ConnectedPropertyRow => row !== null)
        default:
            return []
    }
}

/**
 * Sorts and distributes property data into service-specific arrays based on property service type.
 */
const sortFilterData = (
    acc: ConnectedPropertiesFilterData,
    arr: ConnectedPropertyRow[],
    option1: keyof ConnectedPropertiesFilterData,
    option2: keyof ConnectedPropertiesFilterData
) => {
    arr.forEach((row) => {
        const service = row.service as FormValue
        const targetOption = service?.id === REAL_ESTATE_SERVICE.sale ? option1 : option2
        acc[targetOption].push(row)
    })
}

/**
 * Groups and categorizes property data by service type (sales, lease, purchase, rent) for dropdown filtering.
 */
const mapFilterData = (formattedResponse: Record<ConnectedPropertyKey, ConnectedPropertyRow[]>): ConnectedPropertiesFilterData => {
    const filterData: ConnectedPropertiesFilterData = { sales: [], lease: [], purchase: [], rent: [] }

    sortFilterData(filterData, formattedResponse.contactBuyer, 'purchase', 'rent')
    sortFilterData(filterData, formattedResponse.contactSeller, 'sales', 'lease')
    sortFilterData(filterData, formattedResponse.contactSelectedActivities, 'purchase', 'rent')

    return filterData
}

/**
 * Main handler for processing combined API responses from multiple property/activity endpoints.
 * Transforms and categorizes data from useConnectedProperties hook which aggregates
 * results from buyer, seller, and activity endpoints into a structure suitable for table display and filtering.
 */
export const handleResponse = (data: ConnectedPropertiesResponse): ConnectedPropertiesFilterData => {
    const formattedResponse: Record<ConnectedPropertyKey, ConnectedPropertyRow[]> = {
        contactBuyer: mapTableData(data.contactBuyer, 'contactBuyer'),
        contactSeller: mapTableData(data.contactSeller, 'contactSeller'),
        contactSelectedActivities: mapTableData(data.contactSelectedActivities, 'contactSelectedActivities'),
    }

    return mapFilterData(formattedResponse)
}
